import { cloneDeep, isNil, merge, uniq } from "lodash-es";
import { BehaviorSubject, combineLatest, Observable } from "rxjs";
import { map, switchMap } from "rxjs/operators";

import { memoize } from "../decorators/memoize";

import { CONFIG_ERROR_ATLAS } from "./config.const";
import { ConfigBase, ConfigQuery, ConfigServiceOptions } from "./config.model";

export class ConfigService<C extends ConfigBase<C>> {
	private readonly configStream$: BehaviorSubject<C>;
	private readonly jurisdictionStream$: BehaviorSubject<string>;
	private readonly dimensionsStream$: BehaviorSubject<string[]>;

	constructor(
		private configBase: C,
		private options: ConfigServiceOptions
	) {
		this.configStream$ = new BehaviorSubject<C>(
			this.options.enabledPreprocessor ? this.processConfig() : cloneDeep(this.configBase)
		);
		this.jurisdictionStream$ = new BehaviorSubject<string>(this.options.jurisdiction);
		this.dimensionsStream$ = new BehaviorSubject<string[]>(this.options.dimensions || []);
	}

	get<T>(query: ConfigQuery<C, T>): T;

	get<T>(query: ConfigQuery<C, T>): T | undefined;

	get<T>(query: ConfigQuery<C, T>, defaultValue: T): T;

	get<T>(query: ConfigQuery<C, T>, defaultValue?: T): T;

	get<T>(query: ConfigQuery<C, T>, defaultValue?: T): T | undefined {
		const tenant = this.configStream$.value;
		const value = this.getValue(this.options.jurisdiction, tenant, query, defaultValue);

		if (this.options.enabledPreprocessor) {
			return value;
		}

		return !!this.options.dimensions && this.options.dimensions.length
			? this.processDimensions(value, this.options.jurisdiction, this.options.dimensions, tenant, query)
			: value;
	}

	get$<T>(query: ConfigQuery<C, T>): Observable<T | undefined>;

	get$<T>(query: ConfigQuery<C, T>, defaultValue: T): Observable<T>;

	get$<T>(query: ConfigQuery<C, T>, defaultValue?: T): Observable<T>;

	get$<T>(query: ConfigQuery<C, T>, defaultValue?: T): Observable<T | undefined> {
		return this.configStream$.pipe(
			switchMap((config) =>
				combineLatest([this.jurisdictionStream$, this.dimensionsStream$]).pipe(
					map(([jurisdiction, dimensions]) => [config, jurisdiction, dimensions] as [C, string, string[]])
				)
			),
			map(([config, jurisdiction, dimensions]) => [config, jurisdiction, dimensions] as [C, string, string[]]),
			map(([tenant, jurisdiction, dimensions]) => {
				const value = this.getValue(jurisdiction, tenant, query, defaultValue);

				if (this.options.enabledPreprocessor) {
					return value;
				}

				return this.processDimensions(value, jurisdiction, dimensions, tenant, query);
			})
		);
	}

	updateConfig(configBase: C) {
		this.configBase = configBase;

		this.configStream$.next(this.options.enabledPreprocessor ? this.processConfig() : cloneDeep(configBase));
	}

	updateJurisdiction(jurisdiction: string) {
		this.options.jurisdiction = jurisdiction;

		if (this.options.enabledPreprocessor) {
			this.configStream$.next(this.processConfig());
		}

		this.jurisdictionStream$.next(jurisdiction);
	}

	updateDimensions(...dimensions: string[]) {
		this.options.dimensions = dimensions;

		if (this.options.enabledPreprocessor) {
			this.configStream$.next(this.processConfig());
		}

		this.dimensionsStream$.next(uniq(dimensions) || []);
	}

	private processDimensions<T>(
		inputValue: T,
		jurisdiction: string,
		dimensions: string[],
		tenant: C,
		query: ConfigQuery<C, T>
	) {
		if (!tenant.$dimensions) {
			return inputValue;
		}

		for (const dimension of dimensions) {
			if (!tenant.$dimensions[dimension]) {
				continue;
			}

			try {
				const dimValue = this.getValue(jurisdiction, tenant.$dimensions[dimension] as C, query);
				if (dimValue === undefined) {
					continue;
				}

				inputValue = dimValue;
			} catch {
				/* noop */
			}
		}

		return inputValue;
	}

	private getValue<T>(jurisdiction: string, tenant: C, query: ConfigQuery<C, T>, defaultValue?: T): T | undefined {
		let value: T | undefined;

		try {
			if (this.options.enabledPreprocessor) {
				value = query(tenant);
			} else {
				if (jurisdiction && tenant.$jurisdictions && tenant.$jurisdictions[jurisdiction]) {
					// resolve for a jurisdiction
					try {
						value = query(tenant.$jurisdictions[jurisdiction] as C);
					} catch {
						/* noop */
					}
				}

				if (value === undefined) {
					value = query(tenant);
				}
			}
		} catch {
			/* noop */
		}

		if (value === undefined && isNil(defaultValue)) {
			const stringReference = this.getQueryKey(query);
			// todo: to be removed when we move to vNext - OBGB-11551
			// eslint-disable-next-line no-console
			console.warn(`${CONFIG_ERROR_ATLAS.defaultValueRequired} [${stringReference}]`);
			return undefined;
		}

		return value !== undefined ? value : defaultValue;
	}

	private getQueryKey<T>(query: ConfigQuery<C, T>): string {
		return this.escapeQueryKey(query.toString());
	}

	@memoize()
	private escapeQueryKey(query: string): string {
		return query.replace(/^\r+|\n+|\t+|\s+$|\s+(?=\s)/g, "");
	}

	private processConfig(): C {
		const configBaseClone = cloneDeep(this.configBase);

		const currentJurisdiction =
			this.options.jurisdiction && configBaseClone.$jurisdictions
				? configBaseClone.$jurisdictions[this.options.jurisdiction]
				: {};

		const currentDimensions =
			this.options.dimensions?.length && configBaseClone.$dimensions
				? this.options.dimensions.map((x) => configBaseClone.$dimensions![x]).filter((x) => x)
				: [];

		return merge([configBaseClone, currentJurisdiction || {}, ...currentDimensions]);
	}
}
